import '.././pages.css';
import { useLocation } from 'react-router-dom';
import DailyOverview from '../../components/Cards/DailyOverviewChart';
import ExecutionTrendChart from '../../components/Cards/ExecutionTrendChart';
import DateRangePicker, { computeRangeForPreset } from '../../components/Filters/DateRangePicker';
import FilterPanel from '../../components/Filters/FilterPanel';
import HighFailureRate from '../../components/Cards/HighFailureRate';
import LastSyncDisplay from '../../components/LastSyncDisplay';
import NewFailuresCard from '../../components/Cards/NewFailures';
import React, { useState } from 'react';
import SuccessRateCard from '../../components/Cards/DailySuccessRate';
import TestResultsTable from '../../components/BrokenTestsTable/BrokenTestsTable';
import useFiltersData from '../../hooks/useFiltersData';
import useLastSync from '../../hooks/useLastSync';
import usePackages from '../../hooks/usePackages';
import { formatLocalDate } from '../../utils/dateFormat';

const PLATFORM_LABELS = { null: 'All Platforms', true: 'CSQ XP', false: 'Legacy' };
const PLATFORM_VALUES = { 'All Platforms': null, 'CSQ XP': true, 'Legacy': false };

function Filters({ dateRange, datePreset, setDateRange, selectedEnvironment, selectedPackage, selectedFramework, selectedIsCsqXp, onFilterApply, lastSync, lastSyncLoading, availablePackages, packagesLoading }) {
  const filterFields = [
    {
      key: 'env',
      label: 'Environment',
      options: ['All Environments', 'Local', 'Staging', 'Production'],
      defaultValue: 'All Environments',
    },
    {
      key: 'pkg',
      label: 'Package',
      options: packagesLoading ? [selectedPackage] : (availablePackages || [selectedPackage]),
      defaultValue: 'app',
      disabled: packagesLoading,
    },
    {
      key: 'framework',
      label: 'Framework',
      options: ['All Frameworks', 'Playwright', 'WDIO'],
      defaultValue: 'All Frameworks',
    },
    {
      key: 'platform',
      label: 'Platform',
      options: ['All Platforms', 'CSQ XP', 'Legacy'],
      toLabel: (v) => PLATFORM_LABELS[v],
      toValue: (l) => PLATFORM_VALUES[l],
      defaultValue: null,
    },
  ];

  return (
    <div className="filters-container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <DateRangePicker
          value={dateRange}
          preset={datePreset}
          onChange={setDateRange}
          maxDate={new Date()}
          enabledPresets={['today', 'yesterday', '7d', '14d']}
        />
        <FilterPanel
          fields={filterFields}
          values={{ env: selectedEnvironment, pkg: selectedPackage, framework: selectedFramework, platform: selectedIsCsqXp }}
          onApply={onFilterApply}
        />
      </div>
      <LastSyncDisplay lastSync={lastSync} loading={lastSyncLoading} />
    </div>
  );
}

function TestMonitoring() {
  const [filters, setFilters] = useState(() => {
    const initial = computeRangeForPreset('today');
    return {
      dateRange: initial,
      datePreset: 'today',
      selectedEnvironment: 'All Environments',
      selectedPackage: 'app',
      selectedFramework: 'All Frameworks',
      selectedIsCsqXp: null,
    };
  });

  const location = useLocation();
  const setDateRange = ({ startDate, endDate, preset }) =>
    setFilters(prev => ({ ...prev, dateRange: { startDate, endDate }, datePreset: preset }));
  const handleFilterApply = ({ env, pkg, framework, platform }) =>
    setFilters(prev => ({
      ...prev,
      selectedEnvironment: env,
      selectedPackage: pkg,
      selectedFramework: framework,
      selectedIsCsqXp: platform,
    }));

  const startDate = formatLocalDate(filters.dateRange.startDate);
  const endDate = formatLocalDate(filters.dateRange.endDate);

  const { data, rawData, loading, error } = useFiltersData(
    startDate,
    endDate,
    filters.selectedEnvironment,
    filters.selectedPackage,
    filters.selectedFramework,
    'All Units',
    filters.selectedIsCsqXp
  );

  const { data: lastSync, loading: lastSyncLoading } = useLastSync(
    filters.selectedEnvironment,
    filters.selectedPackage
  );

  const { data: availablePackages, loading: packagesLoading } = usePackages("app");

  return (
    <div className="application-container table-owns-scroll">
      {location.pathname === '/application/tests-monitoring' && (
        <>
          <Filters
            dateRange={filters.dateRange}
            datePreset={filters.datePreset}
            setDateRange={setDateRange}
            selectedEnvironment={filters.selectedEnvironment}
            selectedPackage={filters.selectedPackage}
            selectedFramework={filters.selectedFramework}
            selectedIsCsqXp={filters.selectedIsCsqXp}
            onFilterApply={handleFilterApply}
            lastSync={lastSync}
            lastSyncLoading={lastSyncLoading}
            availablePackages={availablePackages}
            packagesLoading={packagesLoading}
          />
          <div className="flex-container">
            <div className="cards-container">
              {(filters.datePreset === '7d' || filters.datePreset === '14d') ? (
                <ExecutionTrendChart
                  rawData={rawData}
                  loading={loading}
                  error={error}
                />
              ) : (
                <DailyOverview
                  selectedDate={filters.dateRange.endDate}
                  selectedEnvironment={filters.selectedEnvironment}
                  selectedPackage={filters.selectedPackage}
                  selectedFramework={filters.selectedFramework}
                  selectedUnit='All units'
                  data={data}
                  loading={loading}
                  error={error}
                />
              )}
              <SuccessRateCard
                selectedDate={filters.dateRange.endDate}
                selectedEnvironment={filters.selectedEnvironment}
                selectedPackage={filters.selectedPackage}
                selectedFramework={filters.selectedFramework}
                data={data}
                loading={loading}
                error={error}
              />
              <NewFailuresCard
                selectedDate={filters.dateRange.endDate}
                selectedEnvironment={filters.selectedEnvironment}
                selectedPackage={filters.selectedPackage}
                selectedFramework={filters.selectedFramework}
              />
              <HighFailureRate />
            </div>
          </div>
          <TestResultsTable
            selectedDate={filters.dateRange.endDate}
            selectedStartDate={filters.dateRange.startDate}
            selectedEndDate={filters.dateRange.endDate}
            datePreset={filters.datePreset}
            selectedEnvironment={filters.selectedEnvironment}
            selectedPackage={filters.selectedPackage}
            selectedFramework={filters.selectedFramework}
            selectedUnit='All units'
            selectedIsCsqXp={filters.selectedIsCsqXp}
          />
        </>
      )}
    </div>
  );
}

export default TestMonitoring;
